import { Box, Typography } from '@mui/material';
import { green, orange, red, grey, lightBlue } from '@mui/material/colors';
import LightTooltip from '../utils/LightTooltip';
import { UserStatusPingViewModel } from '@/models/User/UserStatusPingViewModel';
import { useMemo } from 'react';
import ConnectionStatus from '@/enums/ConnectionStatus';
import { ActiveLocationIcon } from '@/resources/icons/ActiveLocationIcon';
import { ActiveNoLocationIcon } from '@/resources/icons/ActiveNoLocationIcon';
import { ConnectionLostIcon } from '@/resources/icons/ConnectionLostIcon';
import { EmergencyIcon } from '@/resources/icons/EmergencyIcon';
import { InactiveIcon } from '@/resources/icons/InactiveIcon';
import { t } from 'i18next';
import { StringKeys } from '@/resources/strings/StringKeys';

/**
 * @typedef {Object} UserStatusChipProps
 * @property {boolean} hasEmergency - Whether the user has an active emergency.
 * @property {UserStatusPingViewModel | null | undefined} userStatus - The label to display in the chip.
 */

/**
 *
 * @param {UserStatusChipProps} props
 * @returns a chip with a label and an icon
 */
/**
 * @typedef {Object} UserStatusChipProps
 * @property {boolean} hasEmergency
 * @property {UserStatusPingViewModel | null | undefined} userStatus
 * @property {'small' | 'normal'} [size] - Chip size, defaults to 'small'
 */

const UserStatusChip = ({ hasEmergency, userStatus, size = 'normal' }) => {
  /**
   * Returns the background color for the chip based on state using MUI colors.
   */
  const getBackgroundColor = () => {
    if (hasEmergency) {
      return red[100]; // Emergency
    }
    switch (userStatus?.getCalculatedConnectionStatus()) {
      case ConnectionStatus.Active:
      case ConnectionStatus.CheckingConnection:
        if (userStatus.isSharingLocation()) {
          return green[50]; // Active & sharing location
        } else {
          return lightBlue[50]; // Active but not sharing location
        }
      case ConnectionStatus.Unknown:
        return orange[50]; // Warning/Connection Lost
      case ConnectionStatus.DisconnectedIntentional:
      default:
        return grey[100]; // Inactive
    }
  };
  /**
   * Returns the text color based on the icon background/state using MUI colors.
   */
  const getTextColor = () => {
    if (hasEmergency) {
      return red[700]; // Emergency
    }
    switch (userStatus?.getCalculatedConnectionStatus()) {
      case ConnectionStatus.Active:
      case ConnectionStatus.CheckingConnection:
        if (userStatus.isSharingLocation()) {
          return green[700]; // Active
        } else {
          return lightBlue[700];
        }
      case ConnectionStatus.Unknown:
        return orange[800]; // Warning/Connection Lost
      case ConnectionStatus.DisconnectedIntentional:
      default:
        return grey[500]; // Inactive
    }
  };
  /**
   * The status icon based on the user's connection status and location sharing settings.
   * Displays different icons based on emergency status, connection status, and whether they are sharing location.
   */
  const iconProps =
    size === 'normal'
      ? { fontSize: 'medium', sx: { width: 20, height: 20 } }
      : { fontSize: 'small', sx: { width: 14, height: 14 } };

  const getStatusIcon = useMemo(() => {
    if (hasEmergency) {
      return <EmergencyIcon {...iconProps} />;
    }
    switch (userStatus?.getCalculatedConnectionStatus()) {
      case ConnectionStatus.Active:
      case ConnectionStatus.CheckingConnection:
        if (userStatus.isSharingLocation()) {
          return <ActiveLocationIcon {...iconProps} />;
        } else {
          return <ActiveNoLocationIcon {...iconProps} />;
        }
      case ConnectionStatus.Unknown:
        return <ConnectionLostIcon {...iconProps} />;
      case ConnectionStatus.DisconnectedIntentional:
      default:
        return <InactiveIcon {...iconProps} />;
    }
  }, [userStatus, hasEmergency, size]);

  const getStatusLabel = useMemo(() => {
    if (hasEmergency) {
      return t(StringKeys.EMERGENCY_LABEL);
    }

    switch (userStatus?.getCalculatedConnectionStatus()) {
      case ConnectionStatus.Active:
      case ConnectionStatus.CheckingConnection:
        return t(StringKeys.ACTIVE_LABEL);
      case ConnectionStatus.Unknown:
        return t(StringKeys.CONNECTION_LOST_LABEL);
      case ConnectionStatus.DisconnectedIntentional:

      default:
        return t(StringKeys.INACTIVE_LABEL);
    }
  }, [userStatus, hasEmergency]);

  const getStatusTooltip = useMemo(() => {
    if (hasEmergency) {
      return t(StringKeys.EMERGENCY_TOOLTIP);
    }
    var status = userStatus?.getCalculatedConnectionStatus();
    switch (status) {
      case ConnectionStatus.Active:
      case ConnectionStatus.CheckingConnection:
        if (userStatus.isSharingLocation()) {
          return t(StringKeys.ACTIVE_TOOLTIP);
        } else {
          return t(StringKeys.ACTIVE_NO_LOCATION_TOOLTIP);
        }
      case ConnectionStatus.Unknown:
        return t(StringKeys.CONNECTION_LOST_TOOLTIP);
      case ConnectionStatus.DisconnectedIntentional:
      default:
        return t(StringKeys.INACTIVE_TOOLTIP);
    }
  }, [userStatus, hasEmergency]);

  return (
    <LightTooltip title={getStatusTooltip} placement="top" arrow>
      <Box
        sx={{
          width: 'max-content',
          py: size === 'normal' ? '6px' : '3px',
          pl: size === 'normal' ? '12px' : '6px',
          pr: size === 'normal' ? '6px' : '3px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          color: getTextColor(),
          borderRadius: 50,
          border: 'none',
          backgroundColor: getBackgroundColor(),
          cursor: 'default',
        }}
      >
        <Typography
          variant={size === 'normal' ? 'body2' : 'caption'}
          sx={{
            color: getTextColor(),
            fontSize: size === 'normal' ? '0.85rem' : '0.55rem',
            fontWeight: 'bold',
            textWrap: 'nowrap',
            pr: size === 'normal' ? '6px' : '3px',
          }}
        >
          {getStatusLabel}
        </Typography>
        <Box>{getStatusIcon}</Box>
      </Box>
    </LightTooltip>
  );
};

export default UserStatusChip;
